import { z } from "zod";

import { Equals } from "../utils/typeUtils.js";

import { SecondaryCalculations } from "./generated/dbtSecondaryCalculations.js";

/**
 * Zod mirror of `dbtSecondaryCalculations/secondaryCalculations.json`.
 *
 * The JSON Schema stays the source of truth -- it is what AJV validates dbt metric
 * cell payloads against, and what `json-schema-to-typescript` generates
 * `SecondaryCalculations` from. This schema exists so the `SecondaryCalcs` GraphQL
 * scalar and the `UPDATE_DBT_METRIC_CELL` operation payload can be validated and
 * typed by zod instead of being declared as an open record.
 *
 * `secondaryCalculationsMatchesGeneratedType` below fails to compile if the two
 * drift, so regenerating the types from a changed JSON Schema surfaces here.
 */

const SecondaryCalculationAggregate = z.enum(["min", "max", "sum", "average"]);

const PeriodToDate = z.object({
  function: z.literal("period_to_date"),
  args: z.object({
    alias: z.string().optional(),
    aggregate: SecondaryCalculationAggregate,
    period: z.enum(["day", "week", "month", "quarter", "year"]),
  }),
});

const PeriodOverPeriod = z.object({
  function: z.literal("period_over_period"),
  args: z.object({
    alias: z.string().optional(),
    comparison_strategy: z.enum(["ratio", "difference"]),
    interval: z.number().int(),
  }),
});

const Rolling = z.object({
  function: z.literal("rolling"),
  args: z.object({
    alias: z.string().optional(),
    aggregate: SecondaryCalculationAggregate,
    interval: z.number().int().min(1),
  }),
});

export const SecondaryCalculationsSchema = z.discriminatedUnion("function", [
  PeriodToDate,
  PeriodOverPeriod,
  Rolling,
]);

/**
 * Fails to compile when this schema and the generated type diverge, so a change to
 * the JSON Schema cannot silently leave the scalar validating the old shape.
 */
const secondaryCalculationsMatchesGeneratedType: Equals<
  z.infer<typeof SecondaryCalculationsSchema>,
  SecondaryCalculations
> = true;
void secondaryCalculationsMatchesGeneratedType;
